/**
 * Water Balance Redux Slice
 *
 * Manages state for water balance data including status, history,
 * projections, and farm overview in Redux store.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type {
  WaterBalanceStatus,
  WaterBalanceHistoryResponse,
  WaterBalanceProjectionsResponse,
  FarmWaterBalanceOverview,
} from '@/types/waterBalance';
import {
  getWaterBalanceStatus,
  getWaterBalanceHistory,
  getWaterBalanceProjections,
  getFarmWaterBalanceOverview,
  recalculateWaterBalance,
} from '@/services/waterBalanceApi';

// ============================================================================
// Water Balance State Interface
// ============================================================================

interface WaterBalanceState {
  status: Record<number, WaterBalanceStatus>; // sectorId -> status
  history: Record<string, WaterBalanceHistoryResponse>; // composite key (sectorId-startDate-endDate) -> history
  projections: Record<number, WaterBalanceProjectionsResponse>; // sectorId -> projections
  farmOverview: Record<string, FarmWaterBalanceOverview>; // farmId -> overview
  loading: Record<number, boolean>; // sectorId -> loading state
  /** Farm ids with an overview request in flight — dedupes concurrent
   *  dispatches (page + FarmOverviewGrid both fetch on mount). */
  overviewInFlight: Record<string, boolean>;
  /** History composite keys (sectorId-startDate-endDate) with a request in
   *  flight — dedupes StrictMode double-mount and concurrent chart mounts. */
  historyInFlight: Record<string, boolean>;
  /** Sector ids with a projections request in flight. */
  projectionsInFlight: Record<number, boolean>;
  /** Sector ids with a status request in flight. */
  statusInFlight: Record<number, boolean>;
  error: string | null;
}

const initialWaterBalanceState: WaterBalanceState = {
  status: {},
  history: {},
  projections: {},
  farmOverview: {},
  loading: {},
  overviewInFlight: {},
  historyInFlight: {},
  projectionsInFlight: {},
  statusInFlight: {},
  error: null,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a composite key for history records
 */
const createHistoryKey = (sectorId: number, startDate: string, endDate: string): string => {
  return `${sectorId}-${startDate}-${endDate}`;
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetch current water balance status for a sector
 */
export const fetchWaterBalanceStatus = createAsyncThunk(
  'waterBalance/fetchStatus',
  async (sectorId: number) => {
    const response = await getWaterBalanceStatus(sectorId);
    return { sectorId, status: response };
  },
  {
    // Collapse concurrent dispatches for the same sector (StrictMode
    // double-mount, or the chart + map panel both mounting).
    condition: (sectorId, { getState }) => {
      const st = (getState() as { waterBalance: WaterBalanceState }).waterBalance;
      return !st.statusInFlight[sectorId];
    },
  }
);

/**
 * Fetch historical water balance records for a sector
 */
export const fetchWaterBalanceHistory = createAsyncThunk(
  'waterBalance/fetchHistory',
  async ({ sectorId, startDate, endDate }: { sectorId: number; startDate: string; endDate: string }) => {
    const response = await getWaterBalanceHistory(sectorId, startDate, endDate);
    return { sectorId, startDate, endDate, history: response };
  },
  {
    // Dedupe on the composite key so a re-fetch for a different date range
    // still goes through, but StrictMode/concurrent mounts for the same
    // range collapse into one request.
    condition: ({ sectorId, startDate, endDate }, { getState }) => {
      const st = (getState() as { waterBalance: WaterBalanceState }).waterBalance;
      return !st.historyInFlight[createHistoryKey(sectorId, startDate, endDate)];
    },
  }
);

/**
 * Fetch projected water balance for a sector
 */
export const fetchWaterBalanceProjections = createAsyncThunk(
  'waterBalance/fetchProjections',
  async ({ sectorId, daysAhead, includePlannedIrrigations }: {
    sectorId: number;
    daysAhead?: number;
    includePlannedIrrigations?: boolean;
  }) => {
    const response = await getWaterBalanceProjections(sectorId, daysAhead, includePlannedIrrigations);
    return { sectorId, projections: response };
  },
  {
    // Collapse concurrent dispatches for the same sector.
    condition: ({ sectorId }, { getState }) => {
      const st = (getState() as { waterBalance: WaterBalanceState }).waterBalance;
      return !st.projectionsInFlight[sectorId];
    },
  }
);

/**
 * Fetch water balance overview for all sectors in a farm
 */
export const fetchFarmWaterBalanceOverview = createAsyncThunk(
  'waterBalance/fetchFarmOverview',
  async (farmId: string) => {
    const response = await getFarmWaterBalanceOverview(farmId);
    return { farmId, overview: response };
  },
  {
    // The overview is the page's heavy bulk endpoint (computes status for
    // every sector server-side); collapse concurrent dispatches from the
    // dashboard page and FarmOverviewGrid into one request.
    condition: (farmId, { getState }) => {
      const st = (getState() as { waterBalance: WaterBalanceState }).waterBalance;
      return !st.overviewInFlight[farmId];
    },
  }
);

/**
 * Recalculate water balance for a sector over a date range
 */
export const recalculateWaterBalanceThunk = createAsyncThunk(
  'waterBalance/recalculate',
  async ({ sectorId, startDate, endDate, asyncMode = true }: {
    sectorId: number;
    startDate: string;
    endDate: string;
    asyncMode?: boolean;
  }) => {
    const response = await recalculateWaterBalance(sectorId, startDate, endDate, asyncMode);
    return { sectorId, startDate, endDate, response, asyncMode };
  }
);

// ============================================================================
// Slice
// ============================================================================

const waterBalanceSlice = createSlice({
  name: 'waterBalance',
  initialState: initialWaterBalanceState,
  reducers: {
    clearWaterBalanceError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Water Balance Status
    builder
      .addCase(fetchWaterBalanceStatus.pending, (state, action) => {
        state.loading[action.meta.arg] = true;
        state.statusInFlight[action.meta.arg] = true;
        state.error = null;
      })
      .addCase(fetchWaterBalanceStatus.fulfilled, (state, action) => {
        state.loading[action.payload.sectorId] = false;
        state.statusInFlight[action.payload.sectorId] = false;
        state.status[action.payload.sectorId] = action.payload.status;
      })
      .addCase(fetchWaterBalanceStatus.rejected, (state, action) => {
        // condition() rejections are silent dedupe hits — the original request
        // still owns the loading/in-flight state, so leave it untouched.
        if (action.meta.condition) return;
        state.loading[action.meta.arg] = false;
        state.statusInFlight[action.meta.arg] = false;
        state.error = action.error.message || 'Failed to fetch water balance status';
      });

    // Fetch Water Balance History
    builder
      .addCase(fetchWaterBalanceHistory.pending, (state, action) => {
        state.loading[action.meta.arg.sectorId] = true;
        const { sectorId, startDate, endDate } = action.meta.arg;
        state.historyInFlight[createHistoryKey(sectorId, startDate, endDate)] = true;
        state.error = null;
      })
      .addCase(fetchWaterBalanceHistory.fulfilled, (state, action) => {
        state.loading[action.payload.sectorId] = false;
        const key = createHistoryKey(
          action.payload.sectorId,
          action.payload.startDate,
          action.payload.endDate
        );
        state.historyInFlight[key] = false;
        state.history[key] = action.payload.history;
      })
      .addCase(fetchWaterBalanceHistory.rejected, (state, action) => {
        // condition() rejections are silent dedupe hits — the original request
        // still owns the loading/in-flight state, so leave it untouched.
        if (action.meta.condition) return;
        const { sectorId, startDate, endDate } = action.meta.arg;
        state.loading[sectorId] = false;
        state.historyInFlight[createHistoryKey(sectorId, startDate, endDate)] = false;
        state.error = action.error.message || 'Failed to fetch water balance history';
      });

    // Fetch Water Balance Projections
    builder
      .addCase(fetchWaterBalanceProjections.pending, (state, action) => {
        state.loading[action.meta.arg.sectorId] = true;
        state.projectionsInFlight[action.meta.arg.sectorId] = true;
        state.error = null;
      })
      .addCase(fetchWaterBalanceProjections.fulfilled, (state, action) => {
        state.loading[action.payload.sectorId] = false;
        state.projectionsInFlight[action.payload.sectorId] = false;
        state.projections[action.payload.sectorId] = action.payload.projections;
      })
      .addCase(fetchWaterBalanceProjections.rejected, (state, action) => {
        // condition() rejections are silent dedupe hits — the original request
        // still owns the loading/in-flight state, so leave it untouched.
        if (action.meta.condition) return;
        state.loading[action.meta.arg.sectorId] = false;
        state.projectionsInFlight[action.meta.arg.sectorId] = false;
        state.error = action.error.message || 'Failed to fetch water balance projections';
      });

    // Fetch Farm Water Balance Overview
    builder
      .addCase(fetchFarmWaterBalanceOverview.pending, (state, action) => {
        state.error = null;
        state.overviewInFlight[action.meta.arg] = true;
      })
      .addCase(fetchFarmWaterBalanceOverview.fulfilled, (state, action) => {
        state.overviewInFlight[action.payload.farmId] = false;
        state.farmOverview[action.payload.farmId] = action.payload.overview;
        // Seed per-sector status entries from the overview payload — the
        // backend builds both from the same get_current_status computation,
        // so each WaterBalanceStatusCard can render without firing its own
        // /status request (previously an N+1: 55 extra calls on big farms).
        for (const sector of action.payload.overview.sectors ?? []) {
          state.status[sector.sector_id] = sector;
          state.loading[sector.sector_id] = false;
        }
      })
      .addCase(fetchFarmWaterBalanceOverview.rejected, (state, action) => {
        state.overviewInFlight[action.meta.arg] = false;
        // condition() rejections are silent dedupe hits, not failures.
        if (!action.meta.condition) {
          state.error = action.error.message || 'Failed to fetch farm water balance overview';
        }
      });

    // Recalculate Water Balance
    builder
      .addCase(recalculateWaterBalanceThunk.pending, (state, action) => {
        state.loading[action.meta.arg.sectorId] = true;
        state.error = null;
      })
      .addCase(recalculateWaterBalanceThunk.fulfilled, (state, action) => {
        state.loading[action.payload.sectorId] = false;
        
        // Handle async mode response (task object) vs sync mode response (records)
        const records = action.payload.response?.records;
        
        if (records?.length > 0) {
          // Update history with the recalculated records (sync mode)
          const key = createHistoryKey(
            action.payload.sectorId,
            action.payload.startDate,
            action.payload.endDate
          );
          state.history[key] = {
            sector_id: action.payload.response.sector_id,
            sector_name: action.payload.response.sector_name,
            start_date: action.payload.startDate,
            end_date: action.payload.endDate,
            records: records,
            total_records: records.length,
          };
          
          // Update status with the latest record if available
          const latestRecord = records[records.length - 1];
          state.status[action.payload.sectorId] = {
            sector_id: action.payload.response.sector_id,
            sector_name: action.payload.response.sector_name,
            current_date: latestRecord.date,
            depletion_mm: latestRecord.depletion_end_mm ?? 0,
            depletion_percent: latestRecord.depletion_percent ?? 0,
            taw_mm: latestRecord.taw_mm ?? 0,
            raw_mm: latestRecord.raw_mm ?? 0,
            kc: latestRecord.kc,
            phenological_stage_id: latestRecord.phenological_stage_id,
            needs_irrigation: (latestRecord.depletion_percent ?? 0) >= (latestRecord.raw_mm ?? 0) / (latestRecord.taw_mm ?? 1) * 100,
            last_calculation_date: latestRecord.date,
            last_calculation_at: latestRecord.created_at,
          };
        }
        // In async mode, the response contains a task_id, not records
        // The task polling hook will handle fetching the results when complete
      })
      .addCase(recalculateWaterBalanceThunk.rejected, (state, action) => {
        state.loading[action.meta.arg.sectorId] = false;
        state.error = action.error.message || 'Failed to recalculate water balance';
      });
  },
});

export const { clearWaterBalanceError } = waterBalanceSlice.actions;
export const waterBalanceReducer = waterBalanceSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

export const selectWaterBalanceStatus = (sectorId: number) => (state: { waterBalance: WaterBalanceState }) =>
  state.waterBalance.status[sectorId];

export const selectWaterBalanceHistory = (key: string) => (state: { waterBalance: WaterBalanceState }) =>
  state.waterBalance.history[key];

export const selectWaterBalanceProjections = (sectorId: number) => (state: { waterBalance: WaterBalanceState }) =>
  state.waterBalance.projections[sectorId];

export const selectFarmWaterBalanceOverview = (farmId: string) => (state: { waterBalance: WaterBalanceState }) =>
  state.waterBalance.farmOverview[farmId];

export const selectWaterBalanceLoading = (sectorId: number) => (state: { waterBalance: WaterBalanceState }) =>
  state.waterBalance.loading[sectorId] ?? false;

export const selectWaterBalanceError = (state: { waterBalance: WaterBalanceState }) =>
  state.waterBalance.error;
